import React from "react";
import Container from "@mui/material/Container";
import { useNavigate } from "react-router-dom";

export default function TermsAndConditionsPage() {
  const navigate = useNavigate();

  const sections = [
    {
      id: "acceptance",
      title: "Acceptance of Terms",
      content:
        "By accessing and using this application, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.",
    },
    {
      id: "use-license",
      title: "Use License",
      content:
        "Permission is granted to temporarily download one copy of the materials (information or software) on KoinApp's application for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:",
      details: [
        "Modify or copy the materials",
        "Use the materials for any commercial purpose or for any public display",
        "Attempt to decompile or reverse engineer any software contained on the application",
        "Remove any copyright or other proprietary notations from the materials",
        "Transfer the materials to another person or \"mirror\" the materials on any other server",
      ],
    },
    {
      id: "disclaimer",
      title: "Disclaimer",
      content:
        "The materials on KoinApp's application are provided on an 'as is' basis. KoinApp makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.",
    },
    {
      id: "limitations",
      title: "Limitations of Liability",
      content:
        "In no event shall KoinApp or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on KoinApp's application, even if KoinApp or an authorized representative has been notified orally or in writing of the possibility of such damage.",
    },
    {
      id: "accuracy",
      title: "Accuracy of Materials",
      content:
        "The materials appearing on KoinApp's application could include technical, typographical, or photographic errors. KoinApp does not warrant that any of the materials on the application are accurate, complete, or current. KoinApp may make changes to the materials contained on the application at any time without notice.",
    },
    {
      id: "materials",
      title: "Materials and Links",
      content:
        "KoinApp has not reviewed all of the sites linked to its application and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by KoinApp of the site. Use of any such linked website is at the user's own risk.",
    },
    {
      id: "modifications",
      title: "Modifications",
      content:
        "KoinApp may revise these terms of service for KoinApp's application at any time without notice. By using this application, you are agreeing to be bound by the then current version of these terms of service.",
    },
    {
      id: "payment",
      title: "Campaign Participation & Incentives",
      content:
        "Koin is a user acquisition and engagement platform. Participation in campaigns is voluntary and incentives are NOT guaranteed. Key terms:",
      details: [
        "Incentives are conditional upon successful campaign completion and verification",
        "Verification may take 24-72 hours or longer depending on volume",
        "Rejected submissions may not be resubmitted for payment",
        "Fraud detection may result in account termination and forfeiture of pending payments",
        "All payouts are final and non-refundable once processed",
        "Koin is not liable for payment delays caused by UPI/bank providers",
        "Minimum withdrawal amounts may apply for certain campaigns",
        "Multiple account abuse will result in account suspension",
      ],
    },
    {
      id: "prohibited",
      title: "Prohibited Activities",
      content:
        "You agree not to engage in any of the following activities:",
      details: [
        "Submitting false or fraudulent screenshots/proofs",
        "Using bots or automated tools to manipulate tasks",
        "Attempting to gain unauthorized access to the application",
        "Violating any applicable laws or regulations",
        "Harassing or abusing other users",
        "Scraping or collecting data without authorization",
      ],
    },
    {
      id: "termination",
      title: "Termination of Account",
      content:
        "KoinApp reserves the right to terminate or suspend your account immediately, without prior notice or liability, for any reason, including if you breach any terms of this agreement or engage in prohibited activities.",
    },
    {
      id: "governing",
      title: "Governing Law",
      content:
        "These terms and conditions are governed by and construed in accordance with the laws of India, and you irrevocably submit to the exclusive jurisdiction of the courts in that location.",
    },
    {
      id: "contact",
      title: "Contact Information",
      content:
        "If you have any questions about these Terms and Conditions, please contact us at support@koinapp.co. We will respond to your inquiry within 24 hours.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Container
        maxWidth="md"
        sx={{
          pt: 3,
          pb: 4,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 16L6 10L12 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Terms & Conditions</h1>
          <div className="w-20" />
        </div>

        {/* Content */}
        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.id} className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <h2 className="text-lg font-bold text-gray-900 mb-3">
                {section.title}
              </h2>
              <p className="text-gray-700 leading-relaxed text-sm mb-3">
                {section.content}
              </p>
              {section.details && (
                <ul className="space-y-2 ml-4">
                  {section.details.map((detail, index) => (
                    <li
                      key={index}
                      className="text-gray-700 text-sm flex items-start gap-2"
                    >
                      <span className="text-orange-600 font-bold mt-0.5">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-8 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 text-center">
          <p className="text-gray-700 text-sm mb-3">
            Have questions about our terms?
          </p>
          <a
            href="mailto:support@koinapp.co"
            className="inline-block px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </Container>
    </div>
  );
}
